import { ApiError, cancelEvent, closeEvent, listEvents, type Event } from "@/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DeliveryProgressBar } from "@/components/ui/DeliveryProgressBar";
import { formatDateTime } from "@/lib/format";
import {
  EVENT_TYPE_LABEL,
  eventStatusLabel,
  isOpenEvent,
  lastMailLabel,
  statusTone,
  typeTone,
} from "@/lib/labels";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const SPINE: Record<string, string> = {
  incident: "var(--color-danger)",
  maintenance: "var(--color-amber)",
  info: "var(--color-accent)",
};

type ColumnId = "open" | "draft" | "done";

const COLUMNS: { id: ColumnId; title: string; hint: string }[] = [
  { id: "open", title: "Offen", hint: "Läuft — Mail ist raus, Vorgang nicht zu" },
  { id: "draft", title: "Entwürfe", hint: "Noch nichts gesendet" },
  { id: "done", title: "Erledigt", hint: "Abgeschlossen oder abgebrochen" },
];

function columnOf(event: Event): ColumnId {
  if (event.status === "draft") return "draft";
  if (event.status === "closed" || event.status === "cancelled") return "done";
  return "open";
}

export function BoardPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const items = await listEvents();
    const sorted = [...items].sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    setEvents(sorted);
  }, []);

  useEffect(() => {
    let cancelled = false;
    refresh()
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Ereignisse konnten nicht geladen werden.");
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const grouped = useMemo(() => {
    const buckets: Record<ColumnId, Event[]> = { open: [], draft: [], done: [] };
    for (const event of events ?? []) {
      buckets[columnOf(event)].push(event);
    }
    return buckets;
  }, [events]);

  async function markClosed(event: Event) {
    setBusyId(event.id);
    setError(null);
    try {
      await closeEvent(event.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Abschließen fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  async function markCancelled(event: Event) {
    setBusyId(event.id);
    setError(null);
    try {
      await cancelEvent(event.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Abbrechen fehlgeschlagen.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 py-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold">Vorgänge</h1>
        </div>
        <Button variant="primary" size="sm" onClick={() => navigate("/mails/new")}>
          Neue Mail
        </Button>
      </div>
      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}
      {events === null && !error ? (
        <div className="grid gap-3 md:grid-cols-3">
          <div className="h-40 animate-pulse rounded-lg bg-surface-subtle" />
          <div className="h-40 animate-pulse rounded-lg bg-surface-subtle" />
          <div className="h-40 animate-pulse rounded-lg bg-surface-subtle" />
        </div>
      ) : null}
      {events ? (
        <div className="grid min-h-0 flex-1 gap-3 md:grid-cols-3">
          {COLUMNS.map((col) => (
            <section key={col.id} className="flex min-h-[240px] flex-col rounded-lg border border-hairline bg-surface">
              <header className="border-b border-hairline px-3 py-2">
                <div className="flex items-baseline justify-between gap-2">
                  <h2 className="text-sm font-semibold">{col.title}</h2>
                  <span className="text-[11px] tabular-nums text-muted">{grouped[col.id].length}</span>
                </div>
                <p className="mt-0.5 text-[11px] text-muted">{col.hint}</p>
              </header>
              <ul className="flex flex-1 flex-col gap-2 p-2">
                {grouped[col.id].length === 0 ? (
                  <li className="px-2 py-6 text-center text-xs text-muted">
                    {col.id === "open"
                      ? "Nichts Offen."
                      : col.id === "draft"
                        ? "Keine Entwürfe."
                        : "Noch nichts erledigt."}
                  </li>
                ) : null}
                {grouped[col.id].map((event) => (
                  <li key={event.id}>
                    <article
                      className="status-spine rounded-md border border-hairline bg-bg p-2.5"
                      style={{ ["--spine-color" as string]: SPINE[event.type] ?? "var(--color-hairline)" }}
                    >
                      <Link to={`/events/${event.id}`} className="block min-w-0">
                        <div className="truncate text-sm font-medium">{event.title || "Ohne Titel"}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <Badge tone={typeTone(event.type)}>{EVENT_TYPE_LABEL[event.type]}</Badge>
                          <Badge tone={statusTone(event.status)}>{eventStatusLabel(event)}</Badge>
                        </div>
                        <p className="mt-1.5 text-[11px] text-muted">
                          {lastMailLabel(event.last_phase, event.sent_count ?? 0)}
                          <span className="tabular-nums"> · {formatDateTime(event.updated_at)}</span>
                        </p>
                        <DeliveryProgressBar progress={event.delivery_progress} className="mt-1.5" />
                      </Link>
                      {isOpenEvent(event.status) ? (
                        <Button
                          size="sm"
                          className="mt-2"
                          disabled={busyId === event.id}
                          onClick={() => void markClosed(event)}
                        >
                          {busyId === event.id ? "…" : "Als erledigt markieren"}
                        </Button>
                      ) : null}
                      {event.status === "draft" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="mt-2"
                          disabled={busyId === event.id}
                          onClick={() => void markCancelled(event)}
                        >
                          Verwerfen
                        </Button>
                      ) : null}
                    </article>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  );
}
